import { NgClass, NgTemplateOutlet } from '@angular/common';
import { Component, computed, ElementRef, HostBinding, HostListener, input, OnInit, output, signal, ViewChild } from '@angular/core';
import { DropDownsModule, PreventableEvent } from '@progress/kendo-angular-dropdowns';
import { PopupModule } from '@progress/kendo-angular-popup';
import { groupBy, GroupResult } from '@progress/kendo-data-query';
import { IconContext, IconTextWidget } from '../icon-text';
import { DroplistItem } from './droplist-item';
import { DEFAULT_POPUP_CONFIG, DroplistPopupConfig } from './droplist-popup-config';

const DEFAULT_ITEM_ID = Number.MAX_SAFE_INTEGER;
const FILTER_THRESHOLD = 12;


@Component({
	selector: 'droplist',
	standalone: true,
	imports: [
		DropDownsModule,
		NgClass,
		IconTextWidget,
		PopupModule,
		NgTemplateOutlet
	],
	templateUrl: './droplist.widget.html',
	styleUrls: ['./droplist.widget.scss'],
})
export class DroplistWidget implements OnInit {


	/** The initially selected item, if any. */
	public readonly selectedItem = input<DroplistItem | undefined>(undefined);


	/** The set of items in the list. */
	public readonly items = input.required<DroplistItem[]>();



	/**
	 * If true or if items > 12, then the top of the dropdown will contain a text area to filter the list.
	 * Default is true;
	 */
	public readonly filterable = input(true);


	/**
	 * If present, then a "Select an item..." option will be included with the provided text.
	 * If not present, then the user will not be able to unselect once a selection is made.
	 */
	public readonly optionalText = input<string>('');



	/**
	 * Sort the list by the attribute passed in.
	 */
	public readonly sortBy = input<'uniqueId' | 'text' | 'customSort'>('text');



	/**
	 * Group names are sorted alphabetically by default. Use groupSortValues to specifically
	 * force the order of one or more groups. Group names of the same number will be sorted
	 * alphabetically.  Group names not provided will be set to a very large number so they
	 * are sorted alphabetically after the group names provided here.
	 */
	public readonly groupSortValues = input<{ [index: string]: number } | undefined>(undefined);



	/**
	 * Optionally provide a popup config to have the widget render an icon button that triggers
	 * the droplist in a popup.  Leave the popup config as undefined to render the droplist
	 * inline.
	 */
	public readonly popupConfig = input<DroplistPopupConfig | undefined>(undefined);



	/**
	 * If true then the droplist will be rendered with rounded borders and hiver colors
	 * to distinguish droplists used for navigation from those used for data entry.
	 */
	public readonly navigation = input(false);


	/**
	 * If true, then droplist will be disabled.
	 */
	public readonly readonly = input(false);



	/**
	 * If the droplist has no items(Options), this message will be displayed.
	 */
	public readonly missingDataMessage = input<string>("No items to select");



	/**
	 * When the selection is changed by the user, this event will be raised to indicate
	 * the new selected value.
	 */

	public readonly selectionChange = output<DroplistItem | undefined>();


	@HostBinding('style.display') private displayStyle = 'inline-block';



	public isFilterable = computed(() => this.filterable() && this.items.length > FILTER_THRESHOLD);
	public mergedPopupConfig = computed(() => {
		const popupConfig = this.popupConfig();
		if (popupConfig) {
			const newPopupConfig = { ...DEFAULT_POPUP_CONFIG, ...popupConfig };
			newPopupConfig.iconContext.text = newPopupConfig.message;
			return newPopupConfig;
		}

		return undefined;
	}
	);

	private groupable = signal(false);
	public setup = computed(() => {
		const setup = this.setDataAndSortItems();
		return {
			sortedItems: setup.sortedItems,
			data: signal<GroupResult[] | DroplistItem[]>(setup.data),
		}
	});

	defaultItem: DroplistItem = {
		uniqueId: DEFAULT_ITEM_ID,
		iconContext: {
			iconClass: '',
			text: '',
			hideText: undefined,
			iconStyle: 'fa-regular',
		}
	};


	ngOnInit(): void {
		if (!this.items()) throw new Error(`Missing required attribute: items`);



		//
		// Determine if we need grouping
		//
		this.groupable.set(false);
		for (const item of this.items()) {
			if (item.groupName) {
				this.groupable.set(true);
				break;
			}
		}

		//
		// Add the optional selection if needed
		//

		if (this.optionalText()) {
			this.defaultItem = {
				uniqueId: DEFAULT_ITEM_ID,
				iconContext: {
					iconClass: '',
					text: this.optionalText(),
					iconStyle: 'fa-regular',
					hideText: undefined,
					subText: this.optionalText(),
				}
			};
		}

		if (this.mergedPopupConfig()) {
			this.displayStyle = 'inline-block';
		}
		else {
			this.displayStyle = 'block';
		}


	}


	setDataAndSortItems() {

		//
		// Sort items by group then text
		//
		const sortValues = this.groupSortValues() || {};

		const newItems = this.items().sort((i1, i2) => {
			if (this.groupable() && i1.groupName !== i2.groupName) {
				const i1s = sortValues[i1.groupName ?? ''] ?? Number.MAX_SAFE_INTEGER;
				const i2s = sortValues[i2.groupName ?? ''] ?? Number.MAX_SAFE_INTEGER;
				if (i1s === i2s) return (i1.groupName ?? '') > (i2.groupName ?? '') ? 1 : -1;
				else return i1s > i2s ? 1 : -1;
			}
			else if (this.sortBy() === 'text') {
				if (i1.iconContext.text !== i2.iconContext.text) {
					return i1.iconContext.text > i2.iconContext.text ? 1 : -1;
				}
			}
			else if (this.sortBy() === 'customSort') {
				if (i1.customSort && i2.customSort && i1.customSort !== i2.customSort) {
					return i1.customSort > i2.customSort ? 1 : -1;
				}
			}
			else if (this.sortBy() === 'uniqueId') {
				if (i1.uniqueId && i2.uniqueId && i1.uniqueId !== i2.uniqueId) {
					return i1.uniqueId > i2.uniqueId ? 1 : -1;
				}
			}
			return 0;
		});


		//
		// If grouping, handle it.
		//
		if (this.groupable()) {
			for (const item of newItems) {
				item.groupName = item.groupName ?? '?';
			}

			return {
				sortedItems: newItems,
				data: groupBy(newItems, [{ field: 'groupName' }]),
			}
		}
		else {
			return {
				data: newItems,
				sortedItems: newItems,
			}
		}

	}


	public handleFilter(query: string): void {

		const predicate = (item: DroplistItem) => item.iconContext.text.toLowerCase().indexOf(query.toLowerCase()) >= 0;

		if (this.groupable()) {
			this.setup().data.set(groupBy(this.setup().sortedItems.filter(predicate), [{ field: "groupName" }]));
		}
		else {
			this.setup().data.set(this.setup().sortedItems.filter(predicate));
		}
	}


	public itemDisabled(itemArgs: { dataItem: DroplistItem, index: number }) {
		return !!itemArgs.dataItem?.disabled;
	}


	public valueChangeEvent(selectedItem: DroplistItem) {
		// this.selectedItem = selectedItem;

		if (selectedItem.uniqueId == DEFAULT_ITEM_ID) this.selectionChange.emit(undefined);
		else this.selectionChange.emit(selectedItem);

		if (this.mergedPopupConfig()) this.toggle(false);
	}


	//
	// Button Popup
	//

	public show = false;

	@ViewChild("anchor") public anchor: ElementRef | undefined;
	@ViewChild("popup", { read: ElementRef }) public popup: ElementRef | undefined;

	@HostListener("keydown", ["$event"])
	public keydown(event: KeyboardEvent): void {
		if (event.key === 'Escape' || event.keyCode === 27) {
			this.toggle(false);
		}
	}

	@HostListener("document:click", ["$event"])
	public documentClick(event: Event): void {
		if (event.target && !this.contains(event.target)) {
			this.toggle(false);
		}
	}

	public toggle(show?: boolean): void {
		this.show = show !== undefined ? show : !this.show;
	}

	private contains(target: EventTarget): boolean {
		return (
			this.anchor?.nativeElement.contains(target) ||
			(this.popup ? this.popup.nativeElement.contains(target) : false)
		);
	}


	public createIconContext(value: DroplistItem): IconContext {
		const iconContext: IconContext = { ...value.iconContext, hideText: 'never' };
		if (value.subText) iconContext.subText = value.subText;
		return iconContext;
	}


	public createListValueTemplate(value: DroplistItem): IconContext {

		const iconContext: IconContext = { ...value.iconContext, hideText: 'never' };
		if (value.subText) iconContext.subText = value.subText;


		if (value.groupName) {
			iconContext.text = `${value.groupName} → ${iconContext.text}`;
		}

		return iconContext;
	}


	public onClose(event: PreventableEvent) {
		//
		// preventDefault() will stop the droplist from hiding. Use this
		// to inspect the contents of the list to determine CSS classes.
		//
		/// event.preventDefault();
	}
}
